import type { LevelDefinition } from './Level.ts';

/**
 * Level 4 — nothing is pulling.
 *
 * Every level so far has ended with fluid collected onto a positive magnet. This one has
 * no collector at all: it is proven that *no* attractor placement anywhere on the board
 * solves it — a sweep of 589 positive positions on a 50wu grid found zero, the best of
 * them reaching 48% of the goal. The only magnet that works is a negative one behind the
 * fluid, and what crosses the gap is momentum.
 *
 * That makes it the level where a magnet stops being a destination and becomes a push.
 *
 * The budget for that push is small and worth knowing: a launch peaks around 350-480 wu/s
 * — moving the magnet closer fans the blob out rather than speeding it up — and with the
 * table's friction that buys roughly 300-400 world units of unassisted travel. The span
 * here is 310. A ninety-degree corner would spend all of it, which is why the flight is
 * straight.
 *
 * The sink under the muzzle is what punishes the near-miss: throw from above the barrel
 * rather than behind it and the blob leaves nose-down, dives in, and holds at 31%.
 *
 * Proven headlessly: the intended launch solves in 6.6s, an attractor on the goal settles
 * inside the sink at 0%, and the fluid comes to rest with 51% of it inside the goal — the
 * win is a landing, not a fly-through.
 */
export const level04: LevelDefinition = {
  id: 'level04',
  name: 'The Gap',
  hint: 'Nothing on the far side is pulling. Get behind the fluid and throw it flat.',
  magnetsAvailable: 1,
  fluid: {
    amount: 380,
    source: { x: 246, y: 496 },
    radius: 44,
  },
  goal: { x: 820, y: 1300, radius: 150, type: 'fluid', fraction: 0.4 },
  walls: [
    // Arena.
    { type: 'segment', x1: 100, y1: 200, x2: 900, y2: 200, thickness: 26 },
    { type: 'segment', x1: 900, y1: 200, x2: 900, y2: 1420, thickness: 26 },
    { type: 'segment', x1: 900, y1: 1420, x2: 100, y2: 1420, thickness: 26 },
    { type: 'segment', x1: 100, y1: 1420, x2: 100, y2: 200, thickness: 26 },

    // The barrel, angled down-right, closed at the breech.
    { type: 'segment', x1: 140, y1: 540, x2: 480, y2: 738, thickness: 26 },
    { type: 'segment', x1: 246, y1: 359, x2: 638, y2: 587, thickness: 26 },
    { type: 'segment', x1: 140, y1: 540, x2: 246, y2: 359, thickness: 26 },

    // The sink under the flight path, and the shaft down to the goal beyond it.
    { type: 'segment', x1: 480, y1: 738, x2: 480, y2: 1150, thickness: 26 },
    { type: 'segment', x1: 480, y1: 1150, x2: 790, y2: 1150, thickness: 26 },
    { type: 'segment', x1: 790, y1: 1150, x2: 790, y2: 900, thickness: 26 },
  ],
};
